/**
 * The arithmetic of a vertical drag, kept pure so it can be pinned by tests.
 *
 * Ranks are sparse floats (server steps of 1024): dropping a row between two
 * neighbours takes their midpoint, so ONE pin is written per drag and two
 * devices dragging different rows cannot clobber each other's order.
 */
import type { Pin } from './usePins'

/**
 * The rank for a row dropped between `prev` and `next` (either absent at the
 * edges). Null means the neighbours cannot host a midpoint — a neighbour has
 * no rank yet (pre-rank pins), or the gap has been halved to exhaustion — and
 * the caller renormalizes the whole list instead.
 */
export function rankBetween(prev: number | undefined, next: number | undefined): number | null {
  if (prev === undefined && next === undefined) return 1024
  if (prev === undefined) return next! - 1024
  if (next === undefined) return prev + 1024
  const mid = (prev + next) / 2
  return mid > prev && mid < next ? mid : null
}

/** Fresh, evenly-spaced ranks for an order — the renormalize fallback. */
export function renumber(count: number): number[] {
  return Array.from({ length: count }, (_, i) => (i + 1) * 1024)
}

/**
 * Where a dragged row would land, from geometry: the index whose slot the
 * pointer's centre has crossed. `tops` are the resting row tops (the dragged
 * row measured at drag start), `height` its height, `from` its index, and
 * `deltaY` how far it has moved.
 */
export function targetIndex(tops: number[], height: number, from: number, deltaY: number): number {
  const centre = tops[from]! + height / 2 + deltaY
  let to = from
  for (let i = 0; i < tops.length; i++) {
    if (i === from) continue
    const mid = tops[i]! + height / 2
    if (i < from && centre < mid) {
      to = Math.min(to, i)
    } else if (i > from && centre > mid) {
      to = Math.max(to, i)
    }
  }
  return to
}

/** The person's order: ranked pins ascending, pre-rank pins after, stable. */
export function sortPins(pins: Pin[]): Pin[] {
  return pins
    .map((p, i) => ({ p, i }))
    .sort((a, b) => {
      const ra = a.p.rank ?? Number.POSITIVE_INFINITY
      const rb = b.p.rank ?? Number.POSITIVE_INFINITY
      return ra !== rb ? ra - rb : a.i - b.i
    })
    .map(({ p }) => p)
}
